// La COLA de mensajes del Agente — ADR-2026-09-13-la-cola-de-mensajes-del-agente.
//
// Mientras el agente responde, el usuario puede seguir escribiendo y mandar: el mensaje
// no interrumpe el turno, queda EN COLA arriba de la caja, y sale solo cuando el agente
// queda libre, en el orden en que se encolaron. Hasta tres: más que eso es una lista de
// pendientes, no una conversación.
//
// Un mensaje en cola se lleva SUS adjuntos (ADR-2026-09-13-la-cola-lleva-sus-adjuntos):
// la cola guardaba sólo el texto y los chips quedaban en la caja, así que al salir cada
// mensaje agarraba los adjuntos que hubiera en ESE momento — el segundo de la cola salía
// pelado, y cambiar los chips mientras esperabas cambiaba lo que ya habías mandado.
//
// Puro, para poder probarlo. El estado vive en `ChatAtlasContext`.

import type { ChatAttachment } from '../pages/v2/agentAttachments';

export const MAX_COLA = 3;

/** El lote de fotos enganchado, tal como lo guarda el Chat (el registro con los archivos
 *  vive en un módulo aparte; acá viaja sólo el puntero). */
export interface LoteEnCola {
  id: string;
  cantidad: number;
}

/** Un mensaje esperando su turno, con TODO lo que iba a viajar con él. */
export interface MensajeEnCola {
  texto: string;
  adjuntos: ChatAttachment[];
  loteFotos: LoteEnCola | null;
}

export const hayLugarEnCola = (cola: readonly MensajeEnCola[]): boolean => cola.length < MAX_COLA;

/** Suma un mensaje al final. No entra si está vacío o si la cola está llena. */
export function encolar(
  cola: readonly MensajeEnCola[],
  mensaje: MensajeEnCola,
): { cola: MensajeEnCola[]; encolado: boolean } {
  const limpio = mensaje.texto.trim();
  if (!limpio || !hayLugarEnCola(cola)) return { cola: [...cola], encolado: false };
  return { cola: [...cola, { ...mensaje, texto: limpio }], encolado: true };
}

/** Saca el mensaje en esa posición (el usuario lo descartó). Un índice inválido no toca nada. */
export const quitarDeCola = (
  cola: readonly MensajeEnCola[],
  indice: number,
): MensajeEnCola[] => cola.filter((_, i) => i !== indice);

/** El próximo a mandar y lo que queda. `null` con la cola vacía. */
export function siguienteDeCola(
  cola: readonly MensajeEnCola[],
): { mensaje: MensajeEnCola; resto: MensajeEnCola[] } | null {
  if (cola.length === 0) return null;
  const [mensaje, ...resto] = cola;
  return { mensaje, resto };
}

/** Al detener el turno la cola NO se manda sola: sus textos vuelven a la caja, debajo de
 *  lo que hubiera, para que el usuario decida. */
export const devolverAlBorrador = (
  borrador: string,
  cola: readonly MensajeEnCola[],
): string => [borrador.trim(), ...cola.map((m) => m.texto)].filter(Boolean).join('\n\n');

/**
 * Los adjuntos de toda la cola, sin repetir, para devolverlos a la caja junto con los
 * textos. Se juntan en uno solo porque la caja es una: se pierde a cuál mensaje
 * pertenecía cada uno, pero no se pierde el archivo — que es lo que no se puede rehacer.
 */
export function adjuntosDeLaCola(
  cola: readonly MensajeEnCola[],
  yaEnLaCaja: readonly ChatAttachment[] = [],
): ChatAttachment[] {
  const vistos = new Set(yaEnLaCaja.map((a) => a.id));
  const out: ChatAttachment[] = [];
  for (const m of cola) {
    for (const a of m.adjuntos) {
      if (vistos.has(a.id)) continue;
      vistos.add(a.id);
      out.push(a);
    }
  }
  return out;
}

/** El primer lote de la cola (la caja admite uno solo). */
export const primerLoteDeLaCola = (cola: readonly MensajeEnCola[]): LoteEnCola | null =>
  cola.find((m) => m.loteFotos)?.loteFotos ?? null;
